import { useCallback, useMemo } from 'react';

import { useAuthContext } from '@/_providers';

import {
  stringifyCustomSettingValue,
  updateCachedCustomSetting,
  useCreateCustomSetting,
  useDeleteCustomSetting,
  useUpdateCustomSetting,
} from '../api';
import type { ParsedCustomSetting, UseCustomSettingsReturn } from '../types';

/**
 * Global hook to manage all custom settings for the current user
 * This provides a centralized way to access and modify custom settings
 */
export function useCustomSettings(): UseCustomSettingsReturn {
  const { user } = useAuthContext();
  const settings = useMemo(() => user?.custom_settings || [], [user]);

  const createMutation = useCreateCustomSetting();
  const updateMutation = useUpdateCustomSetting();
  const deleteMutation = useDeleteCustomSetting();

  // Convert settings array to Map for efficient lookups
  const settingsMap = useMemo(() => {
    const map = new Map<string, ParsedCustomSetting>();
    settings.forEach((setting) => {
      map.set(setting.name, setting);
    });
    return map;
  }, [settings]);

  // Get a specific setting value by name
  const getSetting = useCallback(
    <T>(name: string, defaultValue?: T): T | undefined => {
      const setting = settingsMap.get(name);
      if (!setting) return defaultValue;
      return setting.value as T;
    },
    [settingsMap]
  );

  // Update an existing setting
  const updateSetting = useCallback(
    async <T>(name: string, value: T): Promise<void> => {
      const existingSetting = settingsMap.get(name);

      if (!existingSetting) {
        throw new Error(`Setting '${name}' does not exist. Use createSetting instead.`);
      }

      // Optimistic update
      updateCachedCustomSetting(name, stringifyCustomSettingValue(value));

      try {
        await updateMutation.mutateAsync({ id: existingSetting.id, value });
      } catch (error) {
        // Revert optimistic update on error
        updateCachedCustomSetting(name, stringifyCustomSettingValue(existingSetting.value));
        throw error;
      }
    },
    [settingsMap, updateMutation]
  );

  // Create a new setting
  const createSetting = useCallback(
    async <T>(name: string, value: T): Promise<void> => {
      const existingSetting = settingsMap.get(name);

      if (existingSetting) {
        throw new Error(`Setting '${name}' already exists. Use updateSetting instead.`);
      }

      const res = await createMutation.mutateAsync({ name, value });
      updateCachedCustomSetting(name, undefined, res.data);
    },
    [settingsMap, createMutation]
  );

  // Delete a setting
  const deleteSetting = useCallback(
    async (name: string): Promise<void> => {
      const existingSetting = settingsMap.get(name);

      if (!existingSetting) {
        throw new Error(`Setting '${name}' does not exist.`);
      }

      await deleteMutation.mutateAsync({ id: existingSetting.id });
      updateCachedCustomSetting(name, null);
    },
    [settingsMap, deleteMutation]
  );

  return {
    settings: settingsMap,
    getSetting,
    updateSetting,
    createSetting,
    deleteSetting,
  };
}
